import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import RemoveCircleIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/RemoveCircle.js';
import { useTranslation } from 'react-i18next';
import { fetchOrganizationBrandingFileDownloadUrl } from '../../../api/accountsApi';
import {
  AttachedFilesList,
  brandingFileDisplayName,
  ColorSwatchAdornment,
  HEX_COLOR_RE,
  RemoteBrandingFilesList,
  type BrandingRemoteFile,
  type FileEntry,
} from '../../BrandIdentity/brandingShared';
import { normalizeHexColorInput } from '../onboardingBrandColors';
import {
  OnboardingLogoPreviewDialog,
  type OnboardingLogoPreviewTarget,
} from '../OnboardingLogoPreviewDialog';
import { OnboardingInlineAttachRow } from '../OnboardingInlineAttach';
import { OnboardingFieldLabel, OnboardingStepHeader } from '../OnboardingStepHeader';
import { OnboardingStepScrollLayout } from '../OnboardingStepScrollLayout';
import { onboardingOutlinedInputRootSx } from '../../Form/formFieldSpec';
import { useOnboardingLens } from '../onboardingLens';

interface OnboardingBrandColorFieldProps {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  errorMessage?: string;
  labelToInputGap: string;
  fieldHeight: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
}

/** Figma form field 316×64 — helper overlays the 24px gap below without shifting layout. */
function OnboardingBrandColorField({
  id,
  label,
  value,
  placeholder,
  errorMessage,
  labelToInputGap,
  fieldHeight,
  onChange,
  onBlur,
}: OnboardingBrandColorFieldProps) {
  const showError = Boolean(errorMessage);
  const { tokens, layout, typography, colors } = useOnboardingLens();
  const { formField, brand } = layout;
  const helperFont = tokens.semantic.font.text.sm;

  const brandColorInputFieldSx = useMemo(
    () => ({
      '& .MuiOutlinedInput-root': {
        height: formField.inputHeight,
        borderRadius: formField.inputRadius,
        ...typography.body,
        ...onboardingOutlinedInputRootSx,
        '&.MuiInputBase-adornedStart': {
          pl: formField.inputPaddingX,
        },
      },
      '& .MuiOutlinedInput-input': {
        pl: 0,
        pr: formField.inputPaddingX,
        py: formField.inputPaddingY,
        color: colors.typeMuted,
      },
      '& .MuiOutlinedInput-input::placeholder': {
        color: colors.typeMuted,
        opacity: 1,
      },
    }),
    [colors.typeMuted, formField, typography.body],
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: fieldHeight,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: labelToInputGap,
          height: '100%',
        }}
      >
        <OnboardingFieldLabel htmlFor={id} label={label} required />
        <TextField
          id={id}
          fullWidth
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          error={showError}
          variant="outlined"
          sx={brandColorInputFieldSx}
          slotProps={{
            input: {
              startAdornment: (
                <ColorSwatchAdornment
                  value={value}
                  gapAfter={brand.colorPanelSwatchGap}
                />
              ),
            },
          }}
        />
      </Box>
      <FormHelperText
        error={showError}
        sx={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          m: 0,
          minHeight: helperFont.lineHeight.value,
          fontSize: helperFont.fontSize.value,
          lineHeight: helperFont.lineHeight.value,
          letterSpacing: helperFont.letterSpacing.value,
          visibility: showError ? 'visible' : 'hidden',
        }}
      >
        {errorMessage ?? '\u00A0'}
      </FormHelperText>
    </Box>
  );
}

interface OnboardingStepBrandProps {
  orgId?: string;
  brandGuidelines: boolean | null;
  primaryBrandColor: string;
  secondaryBrandColor: string;
  logoEntries: FileEntry[];
  documentEntries: FileEntry[];
  remoteLogos: BrandingRemoteFile[];
  remoteDocuments: BrandingRemoteFile[];
  pendingInactiveFileIds: Set<string>;
  onBrandGuidelinesChange: (value: boolean | null) => void;
  onPrimaryBrandColorChange: (value: string) => void;
  onSecondaryBrandColorChange: (value: string) => void;
  onLogoAdd: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDocumentAdd: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveLocalLogo: (index: number) => void;
  onRemoveLocalDocument: (index: number) => void;
  onRemoveRemoteFile: (id: string) => void;
  showErrors: boolean;
  fieldErrors: {
    logos?: string;
    brandGuidelines?: string;
    brandingFiles?: string;
    primaryBrandColor?: string;
    secondaryBrandColor?: string;
  };
}

function OnboardingBrandFilesArray({
  hasRemote,
  hasLocal,
  remoteList,
  localList,
  divider,
  inputRadius,
}: {
  hasRemote: boolean;
  hasLocal: boolean;
  remoteList: ReactNode;
  localList: ReactNode;
  divider: string;
  inputRadius: string;
}) {
  if (!hasRemote && !hasLocal) return null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: divider,
        borderRadius: inputRadius,
        overflow: 'hidden',
      }}
    >
      {remoteList}
      {localList}
    </Box>
  );
}

function BrandGuidelinesRadioVisual({
  checked,
  size,
  borderWidth,
  indicatorSize,
  typeDefault,
  surface,
}: {
  checked: boolean;
  size: string;
  borderWidth: string;
  indicatorSize: string;
  typeDefault: string;
  surface: string;
}) {
  if (checked) {
    return (
      <Box
        aria-hidden
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: surface,
          border: `${borderWidth} solid ${typeDefault}`,
          boxSizing: 'border-box',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          '&::after': {
            content: '""',
            width: indicatorSize,
            height: indicatorSize,
            borderRadius: '50%',
            backgroundColor: typeDefault,
          },
        }}
      />
    );
  }

  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `${borderWidth} solid ${typeDefault}`,
        backgroundColor: surface,
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    />
  );
}

/** Figma node 16692:5621 — Step 5 Your brand. */
export function OnboardingStepBrand({
  orgId,
  brandGuidelines,
  primaryBrandColor,
  secondaryBrandColor,
  logoEntries,
  documentEntries,
  remoteLogos,
  remoteDocuments,
  pendingInactiveFileIds,
  onBrandGuidelinesChange,
  onPrimaryBrandColorChange,
  onSecondaryBrandColorChange,
  onLogoAdd,
  onDocumentAdd,
  onRemoveLocalLogo,
  onRemoveLocalDocument,
  onRemoveRemoteFile,
  showErrors,
  fieldErrors,
}: OnboardingStepBrandProps) {
  const { t } = useTranslation();
  const { layout, typography, colors, typeDefault, surface, divider, focusRing, tokens } = useOnboardingLens();
  const { formField, formSection, brand } = layout;

  const logoInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [remoteLogoPreviewUrlById, setRemoteLogoPreviewUrlById] = useState<Record<string, string>>({});
  const [logoPreviewTarget, setLogoPreviewTarget] = useState<OnboardingLogoPreviewTarget | null>(null);
  const [colorTouched, setColorTouched] = useState({
    primary: false,
    secondary: false,
  });

  useEffect(() => {
    if (showErrors) {
      setColorTouched({ primary: true, secondary: true });
    }
  }, [showErrors]);

  const visibleRemoteLogos = useMemo(
    () => remoteLogos.filter((file) => !pendingInactiveFileIds.has(file.id)),
    [remoteLogos, pendingInactiveFileIds],
  );
  const visibleRemoteDocuments = useMemo(
    () => remoteDocuments.filter((file) => !pendingInactiveFileIds.has(file.id)),
    [remoteDocuments, pendingInactiveFileIds],
  );

  const visibleRemoteLogoIds = useMemo(
    () => [...visibleRemoteLogos].map((file) => file.id).sort(),
    [visibleRemoteLogos],
  );

  useEffect(() => {
    if (!orgId || visibleRemoteLogoIds.length === 0) {
      setRemoteLogoPreviewUrlById({});
      return;
    }

    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        visibleRemoteLogoIds.map(async (id) => {
          try {
            const { downloadUrl } = await fetchOrganizationBrandingFileDownloadUrl(orgId, id, {
              inline: true,
            });
            return [id, downloadUrl] as const;
          } catch {
            return [id, ''] as const;
          }
        }),
      );
      if (cancelled) return;
      const next: Record<string, string> = {};
      for (const [id, url] of entries) {
        if (url) next[id] = url;
      }
      setRemoteLogoPreviewUrlById(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, visibleRemoteLogoIds]);

  const hasLogoFiles = visibleRemoteLogos.length > 0 || logoEntries.length > 0;
  const hasDocumentFiles = visibleRemoteDocuments.length > 0 || documentEntries.length > 0;

  const radioLabelSx = {
    ...typography.textMd,
    color: typeDefault,
  };
  const brandingGuidelineOptions = [
    { value: 'yes' as const, label: t('onboarding.wizard.step6.yes') },
    { value: 'no' as const, label: t('onboarding.wizard.step6.no') },
  ];

  const logosError =
    showErrors && fieldErrors.logos
      ? t(`onboarding.wizard.step6.errors.${fieldErrors.logos}`)
      : undefined;
  const brandGuidelinesError =
    showErrors && fieldErrors.brandGuidelines
      ? t(`onboarding.wizard.step6.errors.${fieldErrors.brandGuidelines}`)
      : undefined;
  const brandingFilesError =
    showErrors && fieldErrors.brandingFiles
      ? t(`onboarding.wizard.step6.errors.${fieldErrors.brandingFiles}`)
      : undefined;
  const resolveColorFieldError = (
    value: string,
    touched: boolean,
    submitErrorKey?: string,
  ): string | undefined => {
    if (showErrors && submitErrorKey) {
      return t(`onboarding.wizard.step6.errors.${submitErrorKey}`);
    }
    const trimmed = value.trim();
    if (touched && trimmed !== '' && !HEX_COLOR_RE.test(trimmed)) {
      return t('onboarding.wizard.step6.errors.invalid_hex');
    }
    return undefined;
  };

  const primaryColorError = resolveColorFieldError(
    primaryBrandColor,
    colorTouched.primary,
    fieldErrors.primaryBrandColor,
  );
  const secondaryColorError = resolveColorFieldError(
    secondaryBrandColor,
    colorTouched.secondary,
    fieldErrors.secondaryBrandColor,
  );

  const handleLogoThumbnailClick = (payload: OnboardingLogoPreviewTarget) => {
    setLogoPreviewTarget(payload);
  };

  const removeRemoteAction = (file: BrandingRemoteFile) => (
    <IconButton
      size="small"
      onClick={() => onRemoveRemoteFile(file.id)}
      aria-label={t('page.settings.brand_identity.remove_stored_file_aria', {
        name: brandingFileDisplayName(file.filePath, file.originalFilename),
      })}
      sx={{ width: 40, height: 40 }}
    >
      <RemoveCircleIcon size="lg" />
    </IconButton>
  );

  return (
    <OnboardingStepScrollLayout
      px={formSection.paddingX}
      py={formSection.paddingYCompact}
      headerGap={formSection.sectionGap}
      backgroundColor={colors.surfaceDefault}
      header={
        <OnboardingStepHeader
          title={t('onboarding.wizard.step6.title')}
          subtitle={
            brandGuidelines === false
              ? t('onboarding.wizard.step6.subtitle_no')
              : t('onboarding.wizard.step6.subtitle')
          }
        />
      }
      bodySx={{
        display: 'flex',
        flexDirection: 'column',
        gap: formSection.sectionGap,
      }}
    >
      {/* Logo — Figma 16692:5625 / 16692:6036 with files array */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: formField.labelToInputGap }}>
        <OnboardingFieldLabel
          htmlFor="onboarding-brand-logos"
          label={t('onboarding.wizard.step6.logo_label')}
          required
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: hasLogoFiles ? brand.filesGapToAttach : 0,
          }}
        >
          <OnboardingBrandFilesArray
            hasRemote={visibleRemoteLogos.length > 0}
            hasLocal={logoEntries.length > 0}
            divider={divider}
            inputRadius={formField.inputRadius}
            remoteList={
              <RemoteBrandingFilesList
                embedded
                variant="logo"
                files={visibleRemoteLogos}
                logoPreviewUrlByFileId={remoteLogoPreviewUrlById}
                onLogoThumbnailClick={handleLogoThumbnailClick}
                renderActions={removeRemoteAction}
              />
            }
            localList={
              <AttachedFilesList
                embedded
                showTopDivider={visibleRemoteLogos.length > 0}
                showSmallLogoWarning
                smallLogoWarningText={t('onboarding.wizard.step6.logo_small_warning')}
                onLogoThumbnailClick={handleLogoThumbnailClick}
                entries={logoEntries}
                renderActions={(_, index) => (
                  <IconButton
                    size="small"
                    onClick={() => onRemoveLocalLogo(index)}
                    aria-label="Remove logo"
                    sx={{ width: 40, height: 40 }}
                  >
                    <RemoveCircleIcon size="lg" />
                  </IconButton>
                )}
              />
            }
          />
          <OnboardingInlineAttachRow
            label={t('onboarding.wizard.step6.attach_files')}
            formats={t('onboarding.wizard.step6.logo_formats')}
            maxSize={t('onboarding.wizard.step6.logo_max_size')}
            onClick={() => logoInputRef.current?.click()}
          />
        </Box>
        <input
          ref={logoInputRef}
          id="onboarding-brand-logos"
          type="file"
          accept=".jpg,.jpeg,.png,.svg"
          multiple
          hidden
          onChange={onLogoAdd}
        />
        {logosError && (
          <FormHelperText error sx={{ mx: 0 }}>
            {logosError}
          </FormHelperText>
        )}
      </Box>

      {/* Branding guidelines question — Figma 16692:5637 */}
      <FormControl error={Boolean(brandGuidelinesError)} sx={{ gap: formField.labelToInputGap }}>
        <OnboardingFieldLabel
          label={t('onboarding.wizard.step6.has_branding_doc_label')}
          required
        />
        <Box
          role="radiogroup"
          aria-label={t('onboarding.wizard.step6.has_branding_doc_label')}
          sx={{
            display: 'inline-flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: 'fit-content',
            gap: brand.radioGap,
            minHeight: brand.radioHeight,
          }}
        >
          {brandingGuidelineOptions.map((option) => {
            const selected =
              option.value === 'yes'
                ? brandGuidelines === true
                : brandGuidelines === false;
            const isTabStop =
              selected ||
              (brandGuidelines === null && option.value === brandingGuidelineOptions[0].value);

            return (
              <Box
                key={option.value}
                role="radio"
                aria-checked={selected}
                tabIndex={isTabStop ? 0 : -1}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onBrandGuidelinesChange(option.value === 'yes')}
                onKeyDown={(event) => {
                  if (event.key === ' ' || event.key === 'Enter') {
                    event.preventDefault();
                    onBrandGuidelinesChange(option.value === 'yes');
                  }
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: brand.radioLabelGap,
                  py: brand.radioOptionPaddingY,
                  minHeight: brand.radioHeight,
                  cursor: 'pointer',
                  outline: 'none',
                  '&:focus-visible': {
                    boxShadow: focusRing,
                    borderRadius: tokens.semantic.radius.sm.value,
                  },
                }}
              >
                <BrandGuidelinesRadioVisual
                  checked={selected}
                  size={brand.radioSize}
                  borderWidth={brand.radioBorderWidth}
                  indicatorSize={brand.radioIndicatorSize}
                  typeDefault={typeDefault}
                  surface={surface}
                />
                <Typography component="span" sx={radioLabelSx}>
                  {option.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
        {brandGuidelinesError && (
          <FormHelperText error sx={{ mx: 0 }}>
            {brandGuidelinesError}
          </FormHelperText>
        )}
      </FormControl>

      {/* Yes — upload guidelines — Figma 16692:6010 */}
      {brandGuidelines === true && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: formField.labelToInputGap }}>
          <OnboardingFieldLabel label={t('onboarding.wizard.step6.upload_branding_label')} required />
          {hasDocumentFiles && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: brand.filesGapToAttach }}>
              <OnboardingBrandFilesArray
                hasRemote={visibleRemoteDocuments.length > 0}
                hasLocal={documentEntries.length > 0}
                divider={divider}
                inputRadius={formField.inputRadius}
                remoteList={
                  <RemoteBrandingFilesList
                    embedded
                    variant="document"
                    files={visibleRemoteDocuments}
                    renderActions={removeRemoteAction}
                  />
                }
                localList={
                  <AttachedFilesList
                    embedded
                    showTopDivider={visibleRemoteDocuments.length > 0}
                    entries={documentEntries}
                    renderActions={(_, index) => (
                      <IconButton
                        size="small"
                        onClick={() => onRemoveLocalDocument(index)}
                        aria-label="Remove file"
                        sx={{ width: 40, height: 40 }}
                      >
                        <RemoveCircleIcon size="lg" />
                      </IconButton>
                    )}
                  />
                }
              />
            </Box>
          )}
          <OnboardingInlineAttachRow
            label={t('onboarding.wizard.step6.attach_files')}
            formats={t('onboarding.wizard.step6.branding_formats')}
            maxSize={t('onboarding.wizard.step6.branding_max_size')}
            onClick={() => documentInputRef.current?.click()}
          />
          <input
            ref={documentInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            multiple
            hidden
            onChange={onDocumentAdd}
          />
          <Typography
            sx={{
              fontSize: typography.labelRequired.fontSize,
              lineHeight: `${typography.labelRequired.lineHeight}px`,
              letterSpacing: typography.labelRequired.letterSpacing,
              fontWeight: typography.labelRequired.fontWeight,
              color: colors.typeMuted,
            }}
          >
            {t('onboarding.wizard.step6.branding_help')}
          </Typography>
          {brandingFilesError && (
            <FormHelperText error sx={{ mx: 0 }}>
              {brandingFilesError}
            </FormHelperText>
          )}
        </Box>
      )}

      {/* No — brand colors (preview removed per CEE-618 / CEE-630) */}
      {brandGuidelines === false && (
        <Box
          sx={{
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
            width: '100%',
            maxWidth: brand.colorPanelFrameMaxWidth,
            px: brand.colorPanelPaddingX,
            pt: brand.colorPanelPaddingTop,
            pb: brand.colorPanelPaddingBottom,
            border: `1px solid ${divider}`,
            borderRadius: brand.colorPanelBorderRadius,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: brand.colorPanelFormFieldsGap,
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: brand.colorPanelSectionTitleGap,
                width: '100%',
                height: brand.colorPanelSectionHeaderHeight,
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  ...typography.bodyEmphasis,
                  color: typeDefault,
                }}
              >
                {t('onboarding.wizard.step6.brand_colors_section')}
              </Typography>
              <Typography
                sx={{
                  ...typography.textSm,
                  color: typeDefault,
                }}
              >
                {t('onboarding.wizard.step6.brand_colors_subtitle')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: brand.colorPanelColumnGap,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: brand.colorPanelColumnWidth,
                }}
              >
                <OnboardingBrandColorField
                  id="onboarding-primary-color"
                  label={t('onboarding.wizard.step6.primary_color_label')}
                  placeholder={t('onboarding.wizard.step6.color_placeholder')}
                  value={primaryBrandColor}
                  errorMessage={primaryColorError}
                  labelToInputGap={formField.labelToInputGap}
                  fieldHeight={brand.colorPanelFormFieldHeight}
                  onChange={(e) => onPrimaryBrandColorChange(normalizeHexColorInput(e.target.value))}
                  onBlur={() => setColorTouched((prev) => ({ ...prev, primary: true }))}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: brand.colorPanelColumnWidth,
                }}
              >
                <OnboardingBrandColorField
                  id="onboarding-secondary-color"
                  label={t('onboarding.wizard.step6.secondary_color_label')}
                  placeholder={t('onboarding.wizard.step6.color_placeholder')}
                  value={secondaryBrandColor}
                  errorMessage={secondaryColorError}
                  labelToInputGap={formField.labelToInputGap}
                  fieldHeight={brand.colorPanelFormFieldHeight}
                  onChange={(e) => onSecondaryBrandColorChange(normalizeHexColorInput(e.target.value))}
                  onBlur={() => setColorTouched((prev) => ({ ...prev, secondary: true }))}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      <OnboardingLogoPreviewDialog
        open={logoPreviewTarget !== null}
        target={logoPreviewTarget}
        onClose={() => setLogoPreviewTarget(null)}
      />
    </OnboardingStepScrollLayout>
  );
}
